import api from './api';

/**
 * Service for handling authentication-related API calls
 */
const authService = {
  /**
   * Login user with email and password
   * @param {Object} credentials - User credentials (email, password)
   * @returns {Promise} Promise with user data and token
   */
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      // Store token in localStorage
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise} Promise with user data and token
   */
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      // Store token in localStorage if registration includes auto-login
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },

  /**
   * Request password reset email
   * @param {Object} data - Object containing email
   * @param {string} data.email - User's email address
   * @returns {Promise} Promise with success message
   */
  forgotPassword: async (data) => {
    try {
      const response = await api.post('/auth/forgot-password', data);
      return response.data;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  },

  /**
   * Reset password with token
   * @param {Object} data - Reset password data
   * @param {string} data.token - Reset password token
   * @param {string} data.password - New password
   * @returns {Promise} Promise with success message
   */
  resetPassword: async (data) => {
    try {
      const response = await api.post('/auth/reset-password', data);
      return response.data;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  },

  /**
   * Get current user profile from the API
   * @returns {Promise} Promise with user profile data
   */
  getProfile: async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await api.get('/auth/profile');
      // Update stored user data with the latest from the server
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  },
  
  /**
   * Refresh user profile data from the server
   * @returns {Promise<Object|null>} Updated user data or null if not authenticated
   */
  refreshProfile: async () => {
    try {
      if (!authService.isAuthenticated()) {
        return null;
      }
      
      const userData = await authService.getProfile();
      return userData;
    } catch (error) {
      console.error('Error refreshing profile:', error);
      // If we get a 401, clear the auth state
      if (error.response && error.response.status === 401) {
        authService.logout();
      }
      return null;
    }
  },

  /**
   * Logout current user
   */
  logout: () => {
    // Clear auth data from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Clear Authorization header
    delete api.defaults.headers.common['Authorization'];
  },

  /**
   * Get current authenticated user
   * @returns {Object|null} Current user or null if not authenticated
   */
  getCurrentUser: () => {
    try {
      const userString = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      
      if (!userString || !token) return null;

      return JSON.parse(userString);
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user is authenticated
   */
  isAuthenticated: () => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (token && user) {
      return true;
    }
    
    return false;
  },

  /**
   * Get authentication token
   * @returns {string|null} Token or null if not authenticated
   */
  getToken: () => {
    return localStorage.getItem('token');
  },

  /**
   * Check if user has a specific role
   * @param {string} roleName - Role name to check
   * @returns {boolean} True if user has the role
   */
  hasRole: (roleName) => {
    const user = authService.getCurrentUser();
    if (!user || !user.role) return false;
    
    return user.role.name === roleName;
  },

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean} True if user has the permission
   */
  hasPermission: (permission) => {
    const user = authService.getCurrentUser();
    if (!user || !user.role || !user.role.permissions) return false;
    
    return user.role.permissions.includes(permission);
  }
};

export default authService;
